import {useState, useRef} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {addMessage, updateLastMessage, removeLastMessageIfEmpty} from './chatSlice';
import {buildChatRequestPayload} from './chatRequestUtils';
import {fetchPostAuthSafe} from '../../app/utils';
import {getCustomerInfo, getUserAccountInfo} from '../../appSlice';

// Backoff schedule (ms) for re-confirming session-sync values when they are missing,
// e.g. after a transient Redis interruption left userSettings unpopulated. The
// chat send waits and re-fetches on this schedule rather than logging the user out.
const SYNC_RETRY_DELAYS_MS = [1000, 5000, 10000, 15000];

/**
 * Shared hook for sending Insights chat messages and handling SSE response.
 * Used by both the Insights page and the ChatPanel component.
 * @return {{ sendMessage, stopMessage, isLoading, abortControllerRef }}
 */
export function useInsightsChatSend() {
  const dispatch = useDispatch();
  const userSettings = useSelector((state) => {
    return state.app.userSettings;
  });

  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const lastRequestRef = useRef({sessionId: null, threadId: null});

  // Used to read the freshest Redux state after a re-fetch (selector values are
  // captured at render time and won't reflect an in-flight repopulate).
  const store = useStore();

  /**
   * Abort the in-flight chat request. Triggers the AbortError path in handleSendMessage,
   * which keeps any partial streamed text and removes an empty assistant placeholder.
   */
  const stopMessage = () => {
    const {sessionId, threadId} = lastRequestRef.current;
    if (sessionId) {
      fetchPostAuthSafe(
        '/insights/chat/cancel',
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          credentials: 'include',
          body: JSON.stringify({sessionId, threadId}),
        },
        userSettings.username,
        userSettings.databaseName
      ).catch(() => {});
    }
    abortControllerRef.current?.abort();
  };

  /**
   * Resolve the session-sync values required by the server's requireLogin check.
   * Both come from the user record (userSettings), the same source every other page uses for the
   * sync headers. If either is missing (e.g. userSettings was never populated after a transient
   * Redis interruption or a fresh reload), retry re-fetching customer/user info on a
   * backoff schedule and read the freshest values. A transient blip becomes a brief
   * delay rather than a forced logout; if values still can't be resolved after all
   * retries the caller bails gracefully (no logout, message preserved).
   * @return {Promise<{username: (string|undefined), db: (string|undefined)}>}
   */
  async function resolveSyncValues() {
    let username = userSettings?.username;
    let db = userSettings?.databaseName;
    if (username && db) {
      return {username, db};
    }

    for (let i = 0; i < SYNC_RETRY_DELAYS_MS.length; i++) {
      // Stop waiting if the user aborted or navigated away mid-retry.
      if (abortControllerRef.current?.signal.aborted) break;

      console.warn(
        `[useInsightsChatSend] Missing session sync values; retry ${i + 1}/${SYNC_RETRY_DELAYS_MS.length} ` +
          `after ${SYNC_RETRY_DELAYS_MS[i]}ms`
      );
      await new Promise((resolve) => {
        return setTimeout(resolve, SYNC_RETRY_DELAYS_MS[i]);
      });

      await Promise.all([dispatch(getCustomerInfo()), dispatch(getUserAccountInfo())]);
      const app = store.getState().app;
      username = app?.userSettings?.username;
      db = app?.userSettings?.databaseName;
      if (username && db) {
        return {username, db};
      }
    }

    return {username, db};
  }

  /**
   * Send a message to /insights/chat and stream the response into Redux.
   * @param {string} userMessage - Trimmed user message
   * @param {Array} chatHistory - Current Redux chat history (before adding the new user message)
   * @param {Object} [constantOverrides] - Optional constant overrides for the request
   * @param {string} sessionId - Session ID for the chat
   * @param {string} threadId - Thread ID for the chat
   * @param {Array|null} [clarificationPayload] - Structured group definition selections
   * @param {boolearn} abortClarification - trigger to abort the clarifications
   */
  async function sendMessage(
    userMessage,
    chatHistory,
    constantOverrides = null,
    sessionId,
    threadId,
    clarificationPayload = null,
    abortClarification = false
  ) {
    const payload = buildChatRequestPayload({
      message: userMessage,
      chatHistory,
      constantOverrides: constantOverrides ?? undefined,
      sessionId,
      threadId,
      clarificationPayload: clarificationPayload ?? undefined,
      abortClarification,
    });

    const {username, db} = await resolveSyncValues();
    if (!username || !db) {
      throw new Error('Session not ready: customer/user info unavailable. Please retry in a moment.');
    }

    const response = await fetchPostAuthSafe(
      '/insights/chat',
      {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal,
      },
      username,
      db
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    const initialMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      threadId, // Include threadId so reducer can filter by active thread
    };
    dispatch(addMessage(initialMessage));

    let accumulatedContent = '';
    let accumulatedHtml = '';
    let accumulatedQueryResults = null;
    let accumulatedParameters = null;
    let accumulatedDownload = null;
    let accumulatedAnalysisContext = null;

    const updateLast = (overrides = {}) => {
      dispatch(
        updateLastMessage({
          threadId, // Include threadId so reducer can filter by active thread
          content: accumulatedContent,
          queryResults: accumulatedQueryResults,
          parameters: accumulatedParameters,
          analysisContext: accumulatedAnalysisContext || undefined,
          html: accumulatedHtml || undefined,
          download: accumulatedDownload || undefined,
          timestamp: new Date().toISOString(),
          ...overrides,
        })
      );
    };

    const processData = (data) => {
      if (data.queryResults) accumulatedQueryResults = data.queryResults;
      if (data.parameters) accumulatedParameters = data.parameters;
      if (data.analysisContext) accumulatedAnalysisContext = data.analysisContext;
      // Raw SQL data rows as a CSV download, pushed on the stream before the answer.
      if (data.download) {
        accumulatedDownload = data.download;
        updateLast({download: accumulatedDownload});
      }
      if (data.turnId) updateLast({turnId: data.turnId});
      if (data.mode === 'clarification' && data.options) {
        accumulatedContent = data.content || '';
        updateLast({
          mode: 'clarification',
          clarificationType: data.clarificationType || 'choiceSelect',
          options: data.options,
          ...(data.clarificationQuestion ? {clarificationQuestion: data.clarificationQuestion} : {}),
        });
      } else if (data.content) {
        accumulatedContent += data.content;
        updateLast();
      }
      if (data.appliedGroups) {
        updateLast({appliedGroups: data.appliedGroups});
      }
      if (data.html) {
        accumulatedHtml = data.html;
        updateLast({
          content: accumulatedContent || 'Generated visualization',
          html: accumulatedHtml,
        });
      }
      if (data.dataQuery) {
        updateLast({dataQuery: data.dataQuery});
      }
      if (data.error) {
        console.error('SSE error:', data.error);
        accumulatedContent = data.error;
        updateLast({content: accumulatedContent});
      }
    };

    const STREAM_TIMEOUT_MS = 120_000;
    let buffer = '';
    while (true) {
      const {done, value} = await Promise.race([
        reader.read(),
        new Promise((_, reject) => {
          return setTimeout(() => {
            return reject(new Error('Stream timeout'));
          }, STREAM_TIMEOUT_MS);
        }),
      ]);
      if (done) break;

      buffer += decoder.decode(value, {stream: true});
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('status: ')) {
          const statusStr = line.slice(8).trim();
          if (!statusStr) continue;
          const status = JSON.parse(statusStr);
          dispatch(
            updateLastMessage({
              threadId,
              statusUpdate: true,
              status: status,
            })
          );
        }
        if (!line.startsWith('data: ')) continue;
        try {
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;
          const data = JSON.parse(jsonStr);
          processData(data);
          if (data.done) return;
        } catch (e) {
          console.error('Error parsing SSE line:', line, e);
        }
      }
    }

    // Process remaining buffer
    if (buffer.trim()) {
      const lines = buffer.split('\n');
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;
          const data = JSON.parse(jsonStr);
          processData(data);
          if (data.done) break;
        } catch (e) {
          console.error('Error parsing final SSE line:', line, e);
        }
      }
    }
  }

  /**
   * Appends the user message, runs SSE fetch, merges assistant chunks into the last message via Redux.
   * @param {string} userMessage
   * @param {Array} chatHistory - History **before** the new user row (sendMessage re-payloads this + user text).
   * @param {Object|null} [constantOverrides]
   * @param {string} sessionId - Unique session identifier for chat memory.
   * @param {string} threadId - Unique thread identifier for conversation context.
   * @param {Array|null} [clarificationPayload] - Structured group definition selections
   * @param {boolearn} abortClarification - trigger to abort the clarifications
   */
  async function handleSendMessage(
    userMessage,
    chatHistory,
    constantOverrides = null,
    sessionId,
    threadId,
    clarificationPayload = null,
    abortClarification = false
  ) {
    dispatch(
      addMessage({
        threadId,
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString(),
      })
    );

    setIsLoading(true);
    abortControllerRef.current = new AbortController();
    lastRequestRef.current = {sessionId, threadId};

    try {
      await sendMessage(
        userMessage,
        chatHistory,
        constantOverrides,
        sessionId,
        threadId,
        clarificationPayload,
        abortClarification
      );
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
        dispatch(removeLastMessageIfEmpty({threadId}));
      } else {
        console.error('Error sending message:', error);
        dispatch(
          addMessage({
            threadId,
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date().toISOString(),
            error: true,
          })
        );
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }

  return {sendMessage: handleSendMessage, stopMessage, isLoading, abortControllerRef};
}
